import { MatDialog } from '@angular/material/dialog';
import { ClassicPreset } from 'rete';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { ConnectionEvents } from '../connections/models/connection-events';
import { SelectableConnection } from '../connections/models/SelectableConnection';
import { Action } from '../models/action';
import { ActivateStepActionData } from '../models/activate-step-action-data';
import { ChangeStepStateActionData } from '../models/change-step-state-action-data';
import { CompleteStepActionData } from '../models/complete-step-action-data';
import { IWorkflowState } from '../models/Interfaces/workflow-state.interface';
import { NodePosition } from '../models/node-position';
import { ReteContext } from '../models/rete-context';
import { StepActivatedTriggerData } from '../models/step-activated-trigger-data';
import { StepCompletedTriggerData } from '../models/step-complete-trigger-data';
import { StepInput } from '../models/step-input';
import { StepOutput } from '../models/step-output';
import { StepStateChangedTriggerData } from '../models/step-state-change-trigger-data';
import { Trigger } from '../models/trigger';
import { StateChoiceDialogData } from '../nodes/components/workflow-step-node/controls/state-choice-dialog/models/state_choice_dialog_data';
import { StateChoiceDialogComponent } from '../nodes/components/workflow-step-node/controls/state-choice-dialog/state-choice-dialog.component';
import { NodeEvent } from '../nodes/models/node-event';
import { NodeEventTypes } from '../nodes/models/node-event-types';
import { WorkflowStepNode } from '../nodes/models/workflow-step-node';
import { MappingInfos } from './mapping-infos';
import { SelectionHandler } from './selection-handler';
import { SocketSelection } from './socket-selection';

/**
 * Gestionnaire des évènements du designer de workflow.
 */
export class DesignerCallbackHandler {
    /**
     * Contexte d'execution du designer de workflow.
     */
    public context: ReteContext;

    /**
     * Constructeur.
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param selectionHandler : Gestionnaire de la selection du designer de workflow.
     * @param mappingInfos : Informations sur le mapping entre l'id des noeuds et les elements correspondants.
     */
    constructor(
        public dialog: MatDialog,
        private selectionHandler: SelectionHandler,
        private mappingInfos: MappingInfos
    ) {}

    /**
     * Gère les évènements émis par les noeuds d'étape.
     * @param id : Id du noeud à l'origine de l'évènement.
     * @param eventType : évènement émis par le noeud.
     */
    stepNodeCallBack(id: string, event: NodeEvent) {
        let node = this.context.editor.getNode(id) as WorkflowStepNode;

        switch (event.type) {
            case NodeEventTypes.AddActionActivate:
                // Ajout d'une socket d'entrée au noeud étape pour modéliser une action de type 'Activation de l'étape'.
                this.createInputSocket(node, WorkflowActionTypes.ActivateStep, node.workflowStepData.id);
                break;
            case NodeEventTypes.AddActionChangeStep:
                // Ajout d'une socket d'entrée au noeud étape pour modéliser une action de type 'Changement d'état'.
                this.createInputSocket(node, WorkflowActionTypes.ChangeStepState, node.workflowStepData.id);
                break;
            case NodeEventTypes.AddActionComplete:
                // Ajout d'une socket d'entrée au noeud étape pour modéliser une action de type 'Clôture d'étape'.
                this.createInputSocket(node, WorkflowActionTypes.CompleteStep, node.workflowStepData.id);
                break;
            case NodeEventTypes.AddTriggerActivated:
                // Ajout d'une socket de sortie au noeud étape pour modéliser un trigger de type 'Activation de l'étape'.
                this.createOutputSocket(node, WorkflowTriggerTypes.StepActivated, node.workflowStepData.id);
                break;
            case NodeEventTypes.AddTriggerStepChanged:
                this.createOutputSocket(node, WorkflowTriggerTypes.StepStateChanged, node.workflowStepData.id);
                break;
            case NodeEventTypes.AddTriggerCompleted:
                // Ajout d'une socket de sortie au noeud étape pour modéliser un trigger de type 'Clôture d'étape'.
                this.createOutputSocket(node, WorkflowTriggerTypes.StepCompleted, node.workflowStepData.id);
                break;
            case NodeEventTypes.Selected:
                // Selectionne un noeud.
                this.selectionHandler.selectNode(node);
                break;
            case NodeEventTypes.Unselected:
                //console.log(`Noeud ${node.label} deselectionné`);
                break;
            case NodeEventTypes.InputSocketSelected:
                console.log(`Noeud ${node.label} input socket ${event.data} selected`);
                this.selectionHandler.selectSocket(event.data as SocketSelection);
                break;
            case NodeEventTypes.OutputSocketSelected:
                console.log(`Noeud ${node.label} output socket ${event.data} selected`);
                this.selectionHandler.selectSocket(event.data as SocketSelection);
                break;
        }

        this.context.area.update('node', id);
    }

    /**
     * Gère les évènements émis par les noeuds de trigger.
     * @param id : Id du noeud à l'origine de l'évènement.
     * @param eventType : évènement émis par le noeud.
     */
    triggerNodeCallBack(id: string, event: NodeEvent) {
        let node = this.context.editor.getNode(id);

        switch (event.type) {
            case NodeEventTypes.Selected:
                this.selectionHandler.selectNode(node);
                break;
            case NodeEventTypes.Unselected:
                //console.log(`Noeud ${node.label} deselectionné`);
                //this.selectionHandler.unselectCurrentNode();
                break;
        }

        this.context.area.update('node', id);
    }

    /**
     * Gère les évènements émis par les noeuds de trigger.
     * @param id : Id du noeud à l'origine de l'évènement.
     * @param event : évènement émis par le noeud.
     */
    actionNodeCallBack(id: string, event: NodeEvent) {
        let node = this.context.editor.getNode(id);

        switch (event.type) {
            case NodeEventTypes.Selected:
                this.selectionHandler.selectNode(node);
                break;
            case NodeEventTypes.Unselected:
                //console.log(`Noeud ${node.label} deselectionné`);
                //this.selectionHandler.unselectCurrentNode();
                break;
        }

        this.context.area.update('node', id);
    }

    /**
     * Gère les évènements émis par les connexions.
     * @param id : Id de la connexion à l'origine de l'évènement.
     * @param eventType : Type d'évènement émis par la connexion.
     */
    connectionCallBack(id: string, eventType: ConnectionEvents) {
        let connection = this.context.editor.getConnection(id);

        switch (eventType) {
            case ConnectionEvents.Selected:
                console.log(`Connection ${connection.id} selectionné`);
                // this.unselectNode();
                // this.selectedNode = node;
                break;
            case ConnectionEvents.Unselected:
                console.log(`Connection ${connection.id} deselectionné`);
                this.selectionHandler.unselectCurrentConnection();
                break;
        }

        this.context.area.update('node', id);
    }

    /**
     * Gère les clic souris sur les connexions.
     * @param connection : Instance de la connexion sur laquelle l'utilisateur a cliqué.
     * @param selected : Valeur indiquant si la connexion est selectionnée.
     */
    onConnectionClick(connection: SelectableConnection, selected: boolean) {
        if (selected) {
            this.selectionHandler.selectConnection(connection);
        } else {
            this.selectionHandler.unselectCurrentConnection();
        }
    }

    /**
     * Ouvre une boite de dialogue pour selectionner un état.
     * @returns : L'état selectionné.
     */
    public openStateChoice(states: IWorkflowState[]) {
        const dialogRef = this.dialog.open(StateChoiceDialogComponent, {
            data: { states: states, selectedState: null },
            width: '500px',
            panelClass: 'empty-dialog',
        });

        return dialogRef.afterClosed();
    }

    /**
     * Crée une socket d'entrée d'une étape de workflow et crée l'action associée.
     * @param node : Noeud de l'étape.
     * @param actionType : Type d'action.
     * @param stepId : Id de l'étape.
     */
    private createInputSocket(node: WorkflowStepNode, actionType: WorkflowActionTypes, stepId: number) {
        const inputLabels = Object.keys(node.inputs).map((x) => node.inputs[x].label);

        let action = new Action(actionType, new NodePosition(0, 0));
        action.id = this.generateNewActionId();

        let input = new StepInput(action.id);

        switch (action.type) {
            case WorkflowActionTypes.ActivateStep:
                this.createActivateStepActionSocket(inputLabels, input, action, stepId, node);
                break;
            case WorkflowActionTypes.CompleteStep:
                this.createCompleteStepActionSocket(inputLabels, input, action, stepId, node);
                break;
            case WorkflowActionTypes.ChangeStepState:
                this.createChangeStepStateActionSocket(inputLabels, input, action, stepId, node);
                break;
        }
        return input;
    }

    /**
     * Crée une socket pour l'action de changement d'état d'une étape de workflow et sauve le trigger associé.
     * @param inputLabels Tableau des libellés des sockets d'entrée.
     * @param input : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param action : Instance de l'action à sauver.
     * @param node : Noeud de l'étape.
     */
    private createChangeStepStateActionSocket(inputLabels: string[], input: StepInput, action: Action, stepId: number, node: WorkflowStepNode) {
        action.data = new ChangeStepStateActionData();
        action.data.id = action.id;
        action.data.type = WorkflowActionTypes.ChangeStepState;
        (action.data as ChangeStepStateActionData).stepId = stepId;

        const currentStep = this.context.workflow.steps.find((x) => x.id === stepId);
        let availableTriggerStates = currentStep.data.states.filter((x) => inputLabels.findIndex((y) => y === `Changer l'état à '${x.name}'`) === -1);

        // Affiche une boite de dialogue pour selectionner l'état.
        this.openStateChoice(availableTriggerStates).subscribe((result: StateChoiceDialogData) => {
            if (!!result) {
                if (!inputLabels.find((x) => x === `Changer l'état à '${result.selectedState.name}'`)) {
                    // Il n'y a pas de socket pour cet état.
                    input.label = `Changer l'état à '${result.selectedState.name}'`;
                    input.code = action.id;
                    (action.data as ChangeStepStateActionData).stateId = result.selectedState.id;

                    node.addInput(input.code.toString(), new ClassicPreset.Input(new ClassicPreset.Socket(input.label), input.label));

                    this.context.workflow.addAction(action);
                    this.mappingInfos.actionNodeIdMappings.set(action.id, node.id);
                    this.mappingInfos.setStepNodeActionId(node.id, input.code, action.id);
                }
            }
        });
    }

    /**
     * Crée une socket pour l'action de cloture d'étape d'une étape de workflow et sauve le trigger associé.
     * @param inputLabels Tableau des libellés des sockets d'entrée.
     * @param input : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param action : Instance de l'action à sauver.
     * @param node : Noeud de l'étape.
     */
    private createCompleteStepActionSocket(inputLabels: string[], input: StepInput, action: Action, stepId: number, node: WorkflowStepNode) {
        if (!inputLabels.find((x) => x === 'Finalisation')) {
            input.label = 'Finalisation';
            input.code = action.id;
            action.data = new CompleteStepActionData();
            action.data.id = action.id;
            action.data.type = WorkflowActionTypes.CompleteStep;
            (action.data as CompleteStepActionData).stepId = stepId;

            node.addInput(input.code.toString(), new ClassicPreset.Input(new ClassicPreset.Socket(input.label), input.label));

            this.context.workflow.addAction(action);
            this.mappingInfos.actionNodeIdMappings.set(action.id, node.id);
            this.mappingInfos.setStepNodeActionId(node.id, input.code, action.id);
        }
    }

    /**
     * Crée une socket pour l'action d'activation d'étape d'une étape de workflow et sauve le trigger associé.
     * @param inputLabels Tableau des libellés des sockets d'entrée.
     * @param input : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param action : Instance de l'action à sauver.
     * @param node : Noeud de l'étape.
     */
    private createActivateStepActionSocket(inputLabels: string[], input: StepInput, action: Action, stepId: number, node: WorkflowStepNode) {
        if (!inputLabels.find((x) => x === 'Activation')) {
            input.label = 'Activation';
            input.code = action.id;
            action.data = new ActivateStepActionData();
            action.data.id = action.id;
            action.data.type = WorkflowActionTypes.ActivateStep;
            (action.data as ActivateStepActionData).stepId = stepId;

            node.addInput(input.code.toString(), new ClassicPreset.Input(new ClassicPreset.Socket(input.label), input.label));

            this.context.workflow.addAction(action);
            this.mappingInfos.actionNodeIdMappings.set(action.id, node.id);
            this.mappingInfos.setStepNodeActionId(node.id, input.code, action.id);
        }
    }

    /**
     * Crée une socket de sortie d'une étape de workflow et crée le trigger associé.
     * @param node : Noeud de l'étape.
     * @param triggerType : Type de Trigger.
     * @param stepId : Id de l'étape.
     */
    private createOutputSocket(node: WorkflowStepNode, triggerType: WorkflowTriggerTypes, stepId: number) {
        const outputLabels = Object.keys(node.outputs).map((x) => node.outputs[x].label);

        let trigger = new Trigger(triggerType, new NodePosition(0, 0));
        trigger.id = this.generateNewTriggerId();

        let output = new StepOutput(trigger.id);
        switch (triggerType) {
            case WorkflowTriggerTypes.StepActivated:
                this.createStepActivatedTriggerSocket(outputLabels, output, trigger, stepId, node);
                break;
            case WorkflowTriggerTypes.StepCompleted:
                this.createStepCompletedTriggerSocket(outputLabels, output, trigger, stepId, node);
                break;
            case WorkflowTriggerTypes.StepStateChanged:
                this.createStepStateChangeTriggerSocket(trigger, stepId, outputLabels, output, node);
        }
    }

    /**
     * Crée une socket pour le trigger d'activation d'étape d'une étape de workflow et sauve le trigger associé.
     * @param outputLabels Tableau des libellés des sockets de sortie.
     * @param output : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param trigger : Instance du trigger à sauver.
     * @param node : Noeud de l'étape.
     */
    private createStepActivatedTriggerSocket(outputLabels: string[], output: StepOutput, trigger: Trigger, stepId: number, node: WorkflowStepNode) {
        if (!outputLabels.find((x) => x === 'Activée')) {
            output.label = 'Activée';
            output.code = trigger.id;
            trigger.data = new StepActivatedTriggerData();
            trigger.data.id = trigger.id;
            trigger.data.type = WorkflowTriggerTypes.StepActivated;
            (trigger.data as StepActivatedTriggerData).stepId = stepId;

            node.addOutput(output.code.toString(), new ClassicPreset.Output(new ClassicPreset.Socket(output.label), output.label));

            this.context.workflow.addTrigger(trigger);
            this.mappingInfos.triggerNodeIdMappings.set(trigger.id, node.id);
            this.mappingInfos.setStepNodeTriggerId(node.id, output.code, trigger.id);
        }
    }

    /**
     * Crée une socket pour le trigger de cloture d'étape d'une étape de workflow et sauve le trigger associé.
     * @param outputLabels Tableau des libellés des sockets de sortie.
     * @param output : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param trigger : Instance du trigger à sauver.
     * @param node : Noeud de l'étape.
     */
    private createStepCompletedTriggerSocket(outputLabels: string[], output: StepOutput, trigger: Trigger, stepId: number, node: WorkflowStepNode) {
        if (!outputLabels.find((x) => x === 'Finalisée')) {
            output.label = 'Finalisée';
            output.code = trigger.id;
            trigger.data = new StepCompletedTriggerData();
            trigger.data.id = trigger.id;
            trigger.data.type = WorkflowTriggerTypes.StepCompleted;
            (trigger.data as StepCompletedTriggerData).stepId = stepId;

            node.addOutput(output.code.toString(), new ClassicPreset.Output(new ClassicPreset.Socket(output.label), output.label));

            this.context.workflow.addTrigger(trigger);
            this.mappingInfos.triggerNodeIdMappings.set(trigger.id, node.id);
            this.mappingInfos.setStepNodeTriggerId(node.id, output.code, trigger.id);
        }
    }

    /**
     * Crée une socket pour le trigger de changement d'état d'une étape de workflow et sauve le trigger associé.
     * @param outputLabels Tableau des libellés des sockets de sortie.
     * @param output : Instance de la socket à créer.
     * @param stepId : Id de l'étape.
     * @param trigger : Instance du trigger à sauver.
     * @param node : Noeud de l'étape.
     */
    private createStepStateChangeTriggerSocket(trigger: Trigger, stepId: number, outputLabels: string[], output: StepOutput, node: WorkflowStepNode) {
        trigger.data = new StepStateChangedTriggerData();
        trigger.data.id = trigger.id;
        trigger.data.type = WorkflowTriggerTypes.StepStateChanged;
        (trigger.data as StepStateChangedTriggerData).stepId = stepId;

        const currentStep = this.context.workflow.steps.find((x) => x.id === stepId);
        let availableTriggerStates = currentStep.data.states.filter((x) => outputLabels.findIndex((y) => y === `Etat à '${x.name}'`) === -1);

        // Affiche une boite de dialogue pour selectionner l'état.
        this.openStateChoice(availableTriggerStates).subscribe((result: StateChoiceDialogData) => {
            if (!!result) {
                if (!outputLabels.find((x) => x === `Etat à '${result.selectedState.name}'`)) {
                    // Il n'y a pas de socket pour cet état.
                    output.label = `Etat à '${result.selectedState.name}'`;
                    output.code = trigger.id;
                    (trigger.data as StepStateChangedTriggerData).stateId = result.selectedState.id;

                    node.addOutput(output.code.toString(), new ClassicPreset.Output(new ClassicPreset.Socket(output.label), output.label));

                    this.context.workflow.addTrigger(trigger);
                    this.mappingInfos.triggerNodeIdMappings.set(trigger.id, node.id);
                    this.mappingInfos.setStepNodeTriggerId(node.id, output.code, trigger.id);
                }
            }
        });
    }

    public generateNewTriggerId() {
        let nextTriggerId = 0;

        for (let trigger of this.context.workflow.triggers) {
            if (nextTriggerId <= Number(trigger.id)) {
                nextTriggerId = Number(trigger.id) + 1;
            }
        }

        return nextTriggerId;
    }

    public generateNewActionId() {
        let nextActionId = 0;

        for (let action of this.context.workflow.actions) {
            if (nextActionId <= Number(action.id)) {
                nextActionId = Number(action.id) + 1;
            }
        }

        return nextActionId;
    }
}
